import { useState, useEffect, useCallback } from "react";
import api, { getApiErrorMessage } from "../../lib/api";
import { fetchRewardsByIds } from "../../lib/reward-api";
import { useRewardCatalog } from "../../hooks/useRewardCatalog";
import CatalogPagination from "../../components/CatalogPagination";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatPoints, formatRewardType } from "../../lib/display";
import RewardPhoto from "../../components/RewardPhoto";
import { Gift, Coins, Loader2, CreditCard, Phone, ShoppingBag, Ticket, CheckCircle, Clock, XCircle, Search } from "lucide-react";

const typeIcons = {
  PIX: CreditCard,
  recarga: Phone,
  brinde: Gift,
  produto: ShoppingBag,
  cupom: Ticket,
};

const statusConfig = {
  pendente: { label: "Pendente", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  aprovado: { label: "Aprovado", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
  pago: { label: "Pago", color: "bg-green-100 text-green-700", icon: CheckCircle },
  cancelado: { label: "Cancelado", color: "bg-red-100 text-red-700", icon: XCircle },
};

const getCategoryKey = (reward) => String(reward.tipo_recompensa || "").trim().toLowerCase() || "outros";
const hasRewardStock = (reward) => reward.estoque == null || Number(reward.estoque) > 0;
const canRedeemReward = (reward, balance) => hasRewardStock(reward) && balance >= Number(reward.pontos_necessarios || 0);

const PartnerResgates = () => {
  const { user } = useAuth();
  const [historyRewards, setHistoryRewards] = useState([]);
  const [resgates, setResgates] = useState([]);
  const [parceiro, setParceiro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRecompensa, setSelectedRecompensa] = useState(null);
  const [detailedReward, setDetailedReward] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pixKey, setPixKey] = useState("");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const catalog = useRewardCatalog({ search, category, enabled: Boolean(user?.parceiro_id) });
  const categories = catalog.categorias;
  const visibleRewards = catalog.itens;
  const balance = Number(parceiro?.saldo_pontos || 0);

  const fetchData = useCallback(async () => {
    try {
      const [resgatesRes, parceiroRes] = await Promise.all([
        user?.parceiro_id ? api.get(`/resgates?parceiro_id=${user.parceiro_id}`) : Promise.resolve({ data: [] }),
        user?.parceiro_id ? api.get(`/parceiros/${user.parceiro_id}`) : Promise.resolve({ data: null }),
      ]);
      setResgates(resgatesRes.data);
      setParceiro(parceiroRes.data);
      const details = await fetchRewardsByIds(resgatesRes.data.slice(0, 5).map((resgate) => resgate.recompensa_id)).catch(() => ({ data: [] }));
      setHistoryRewards(details.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(getApiErrorMessage(error, "Erro ao carregar recompensas."));
    } finally {
      setLoading(false);
    }
  }, [user?.parceiro_id]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const openResgate = (recompensa) => {
    if (!hasRewardStock(recompensa)) {
      toast.error("Recompensa sem estoque");
      return;
    }
    if (balance < Number(recompensa.pontos_necessarios || 0)) {
      toast.error("Saldo de pontos insuficiente");
      return;
    }
    setSelectedRecompensa(recompensa);
    setPixKey("");
    setDialogOpen(true);
  };

  const handleResgate = async () => {
    if (!selectedRecompensa || !user?.parceiro_id) return;
    
    setSubmitting(true);
    try {
      const dados_pagamento = selectedRecompensa.tipo_recompensa === "PIX" 
        ? { chave_pix: pixKey }
        : {};

      await api.post("/resgates", {
        parceiro_id: user.parceiro_id,
        recompensa_id: selectedRecompensa.id,
        dados_pagamento,
      });

      toast.success("Resgate solicitado com sucesso!");
      setDialogOpen(false);
      fetchData();
      catalog.refresh();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao solicitar resgate."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-6 animate-fade-in" data-testid="partner-resgates">
      {/* Balance */}
      <div className="bg-white rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm text-slate-500">Seu saldo</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Coins className="h-5 w-5 shrink-0 text-yellow-500" />
              <span className="break-all font-heading text-2xl font-bold text-slate-900">
                {formatPoints(parceiro?.saldo_pontos)}
              </span>
              <span className="text-slate-500">pontos</span>
            </div>
          </div>
        </div>
      </div>

      {/* My Redemptions */}
      {resgates.length > 0 && (
        <div>
          <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">
            Meus Resgates
          </h2>
          <div className="space-y-3">
            {resgates.slice(0, 5).map((resgate) => {
              const recompensa = historyRewards.find(r => r.id === resgate.recompensa_id);
              const config = statusConfig[resgate.status];
              const StatusIcon = config?.icon || Clock;
              
              return (
                <div key={resgate.id} className="min-w-0 rounded-xl bg-white p-4 shadow-sm">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      <RewardPhoto
                        src={recompensa?.foto_base64}
                        alt={resgate.recompensa_nome || recompensa?.nome || "Recompensa"}
                        className="h-12 w-12 shrink-0 rounded-lg"
                        iconClassName="h-5 w-5"
                      />
                      <div className="min-w-0">
                        <p className="break-words font-medium text-slate-900">
                          {resgate.recompensa_nome || recompensa?.nome || "Recompensa"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(resgate.created_at).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex w-fit shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${config?.color}`}>
                      <StatusIcon className="h-3 w-3" />
                      {config?.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Rewards */}
      <div>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="font-heading font-semibold text-lg text-slate-900">
            Catálogo de Recompensas
          </h2>
          <div className="w-full space-y-1 sm:w-60">
            <Label htmlFor="reward-category">Categoria</Label>
            <select
              id="reward-category"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              className="input-field h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900"
            >
              <option value="">Todas as categorias ({categories.reduce((total, item) => total + item.count, 0)})</option>
              {category && !categories.some((item) => item.key === category) && <option value={category}>{formatRewardType(category)} (0)</option>}
              {categories.map((item) => <option key={item.key} value={item.key}>{item.label} ({item.count})</option>)}
            </select>
          </div>
        </div>
        <div className="relative mb-3 max-w-md"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input aria-label="Buscar recompensas" placeholder="Buscar recompensas..." maxLength={120} value={search} onChange={(event) => setSearch(event.target.value)} className="input-field pl-10" /></div>
        <CatalogPagination {...catalog} onPageChange={catalog.setPage} anchor />
        {catalog.error ? <div role="alert" className="flex flex-wrap items-center gap-3 py-8 text-red-700">{catalog.error}<Button variant="outline" onClick={catalog.refresh}>Tentar novamente</Button></div> : catalog.loading ? <div className="flex min-h-72 items-center justify-center" aria-label="Carregando recompensas"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> : <div className="grid grid-cols-1 gap-4">
          {visibleRewards.map((recompensa) => {
            const Icon = typeIcons[recompensa.tipo_recompensa] || Gift;
            const canRedeem = canRedeemReward(recompensa, balance);
            const categoryLabel = categories.find((item) => item.key === getCategoryKey(recompensa))?.label;
            
            return (
              <div
                key={recompensa.id}
                role="button"
                tabIndex={0}
                aria-label={`Ver detalhes de ${recompensa.nome}`}
                data-testid={`reward-card-${recompensa.id}`}
                className={`min-w-0 cursor-pointer rounded-lg bg-white p-5 shadow-sm transition-all focus-visible:ring-2 focus-visible:ring-primary ${
                  canRedeem ? "hover:shadow-md" : "opacity-80"
                }`}
                onClick={() => setDetailedReward(recompensa)}
                onKeyDown={(event) => { if (event.target === event.currentTarget && ["Enter", " "].includes(event.key)) { event.preventDefault(); setDetailedReward(recompensa); } }}
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <RewardPhoto
                    src={recompensa.foto_base64}
                    alt={recompensa.nome}
                    icon={Icon}
                    className="aspect-[16/10] w-full shrink-0 sm:h-24 sm:w-32"
                    iconClassName="h-8 w-8"
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="break-words font-heading font-semibold text-slate-900">
                      {recompensa.nome}
                    </h3>
                    <p className="mt-1 text-xs text-slate-500">{categoryLabel}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Coins className="h-4 w-4 shrink-0 text-yellow-500" />
                      <span className="break-all font-bold text-slate-900">
                        {formatPoints(recompensa.pontos_necessarios)}
                      </span>
                      <span className="text-sm text-slate-500">pontos</span>
                    </div>
                  </div>
                  <Button size="sm" disabled={!canRedeem} onClick={(event) => { event.stopPropagation(); openResgate(recompensa); }} className="btn-primary w-full shrink-0 sm:w-auto" data-testid={`resgate-btn-${recompensa.id}`}>
                    {!hasRewardStock(recompensa) ? "Sem estoque" : canRedeem ? "Resgatar" : "Faltam pontos"}
                  </Button>
                </div>
              </div>
            );
          })}
          {visibleRewards.length === 0 && (
            <div className="bg-slate-50 rounded-2xl p-8 text-center text-slate-500">
              {search || category ? "Nenhuma recompensa encontrada" : "Nenhuma recompensa disponível no momento"}
            </div>
          )}
        </div>}
        <CatalogPagination {...catalog} onPageChange={catalog.setPage} />
      </div>

      <Dialog open={Boolean(detailedReward)} onOpenChange={(open) => !open && setDetailedReward(null)}>
        <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-lg" aria-describedby={undefined}>
          {detailedReward && <><DialogHeader><DialogTitle className="break-words pr-5 text-left">{detailedReward.nome}</DialogTitle></DialogHeader>
            <RewardPhoto src={detailedReward.foto_base64} alt={detailedReward.nome} className="h-60 w-full rounded-lg bg-white" imageClassName="object-contain" />
            <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-600">{detailedReward.descricao || "Sem descrição cadastrada."}</p>
            <p className="font-semibold text-slate-900">{formatPoints(detailedReward.pontos_necessarios)} pontos</p>
            <Button disabled={!canRedeemReward(detailedReward, balance)} onClick={() => { setDetailedReward(null); openResgate(detailedReward); }} className="btn-primary">{!hasRewardStock(detailedReward) ? "Sem estoque" : canRedeemReward(detailedReward, balance) ? "Resgatar" : "Faltam pontos"}</Button>
          </>}
        </DialogContent>
      </Dialog>

      {/* Redemption Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Confirmar Resgate</DialogTitle>
          </DialogHeader>
          {selectedRecompensa && (
            <div className="space-y-6 mt-4">
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <RewardPhoto
                    src={selectedRecompensa.foto_base64}
                    alt={selectedRecompensa.nome}
                    icon={typeIcons[selectedRecompensa.tipo_recompensa] || Gift}
                    className="aspect-[16/10] w-full shrink-0 sm:h-24 sm:w-28"
                    iconClassName="h-7 w-7"
                  />
                  <div className="min-w-0">
                    <p className="break-words font-heading font-semibold text-slate-900">
                      {selectedRecompensa.nome}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <Coins className="h-4 w-4 shrink-0 text-yellow-500" />
                      <span className="break-all font-semibold text-slate-900">
                        {formatPoints(selectedRecompensa.pontos_necessarios)}
                      </span>
                      <span className="text-sm text-slate-500">pontos</span>
                    </div>
                  </div>
                </div>
              </div>

              {selectedRecompensa.tipo_recompensa === "PIX" && (
                <div className="space-y-2">
                  <Label htmlFor="pix">Chave PIX para recebimento</Label>
                  <Input
                    id="pix"
                    placeholder="CPF, email, telefone ou chave aleatória"
                    value={pixKey}
                    onChange={(e) => setPixKey(e.target.value)}
                    className="input-field"
                    data-testid="pix-key-input"
                  />
                </div>
              )}

              <div className="bg-yellow-50 rounded-xl p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Atenção:</strong> Após confirmar, {formatPoints(selectedRecompensa.pontos_necessarios)} pontos serão debitados do seu saldo.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleResgate}
                  disabled={submitting || (selectedRecompensa.tipo_recompensa === "PIX" && !pixKey)}
                  className="btn-primary flex-1"
                  data-testid="confirm-resgate-btn"
                >
                  {submitting ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    "Confirmar"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerResgates;
